const MD_NS = "urn:oasis:names:tc:SAML:2.0:metadata";
const DS_NS = "http://www.w3.org/2000/09/xmldsig#";
const HTTP_REDIRECT =
  "urn:oasis:names:tc:SAML:2.0:bindings:HTTP-Redirect";
const HTTP_POST = "urn:oasis:names:tc:SAML:2.0:bindings:HTTP-POST";

const emptyResult = () => ({
  entityID: "",
  singleSignOnService: "",
  singleLogoutService: "",
  x509Cert: "",
});

const getElements = (doc, ns, localName) => {
  const byNs = Array.from(doc.getElementsByTagNameNS(ns, localName));
  if (byNs.length) return byNs;
  return Array.from(doc.getElementsByTagName(localName)).concat(
    Array.from(doc.getElementsByTagName(`md:${localName}`)),
    Array.from(doc.getElementsByTagName(`ds:${localName}`))
  );
};

const pickServiceLocation = (services) => {
  if (!services.length) return "";

  const byBinding = (binding) =>
    services.find((el) => el.getAttribute("Binding") === binding);

  const preferred =
    byBinding(HTTP_REDIRECT) || byBinding(HTTP_POST) || services[0];

  return preferred?.getAttribute("Location") || "";
};

const pickCertificate = (doc) => {
  const keyDescriptors = getElements(doc, MD_NS, "KeyDescriptor");

  const preferSigning = [...keyDescriptors].sort((a, b) => {
    const aUse = a.getAttribute("use") || "";
    const bUse = b.getAttribute("use") || "";
    if (aUse === "signing" && bUse !== "signing") return -1;
    if (bUse === "signing" && aUse !== "signing") return 1;
    return 0;
  });

  for (const keyDescriptor of preferSigning) {
    const certs = [
      ...Array.from(
        keyDescriptor.getElementsByTagNameNS(DS_NS, "X509Certificate")
      ),
      ...Array.from(keyDescriptor.getElementsByTagName("X509Certificate")),
      ...Array.from(keyDescriptor.getElementsByTagName("ds:X509Certificate")),
    ];
    const raw = certs[0]?.textContent || "";
    const cleaned = raw.replace(/\s+/g, "");
    if (cleaned) return cleaned;
  }

  const fallbackCerts = getElements(doc, DS_NS, "X509Certificate");
  return (fallbackCerts[0]?.textContent || "").replace(/\s+/g, "");
};

/**
 * Parses SAML IdP metadata XML and extracts known fields.
 * Missing values are returned as empty strings.
 */
export const parseSamlMetadataXml = (xmlString) => {
  const result = emptyResult();
  if (!xmlString || !String(xmlString).trim()) return result;

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xmlString, "text/xml");

    if (doc.querySelector("parsererror")) return result;

    const entityDescriptor = getElements(doc, MD_NS, "EntityDescriptor")[0];
    if (entityDescriptor) {
      result.entityID = entityDescriptor.getAttribute("entityID") || "";
    }

    result.singleSignOnService = pickServiceLocation(
      getElements(doc, MD_NS, "SingleSignOnService")
    );
    result.singleLogoutService = pickServiceLocation(
      getElements(doc, MD_NS, "SingleLogoutService")
    );
    result.x509Cert = pickCertificate(doc);
  } catch (_) {
    return emptyResult();
  }

  return result;
};
